import { CanonicalLink as Link } from "@/components/CanonicalLink";
import { Droplet, MapPin, Phone, Mail, Star, Clock } from "lucide-react";
import { services } from "@/data/services";
import { cities } from "@/data/cities";
import { googleReviewSummary } from "@/data/googleReviews";
import { BUSINESS_NAME, BUSINESS_PHONE, BUSINESS_PHONE_DISPLAY, BUSINESS_HOURS_LABEL, businessAddress } from "@/lib/seo";

function scrollTop() { window.scrollTo({ top: 0, behavior: "instant" }); }

export function Footer() {
  const footerServices = [...services.slice(0, 6), ...services.filter(s => s.category === "projects")];

  return (
    <footer className="bg-primary text-primary-foreground pt-16 pb-24 md:pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Brand */}
          <div className="space-y-4">
            <Link href="/" onClick={scrollTop} className="flex items-center gap-2 inline-block">
              <div className="bg-white text-primary p-2 rounded-lg">
                <Droplet className="h-6 w-6" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-bold text-2xl tracking-tight">
                  Plomberie <span className="text-accent-on-dark">Excellence</span>
                </span>
              </div>
            </Link>
            <p className="font-semibold text-white">{BUSINESS_NAME}</p>
            <p className="text-primary-foreground/70 mt-4 leading-relaxed">
              Urgences, réparations et installations de plomberie à Longueuil et sur la Rive-Sud. Disponible 24/7.
            </p>
            <Link href="/a-propos/" onClick={scrollTop} className="inline-block text-sm text-primary-foreground/80 underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
              À propos de Plomberie Excellence
            </Link>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Nous joindre</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-primary-foreground/80">
                <Phone className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <a href={`tel:${BUSINESS_PHONE}`} className="hover:text-white transition-colors font-medium">
                  {BUSINESS_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/80">
                <Mail className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <a href="mailto:plomberieexcellence.contact@gmail.com" className="hover:text-white transition-colors break-all text-sm">
                  plomberieexcellence.contact@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/80">
                <MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <span>
                  {businessAddress.streetAddress}<br />
                  {businessAddress.addressLocality}, {businessAddress.addressRegion} {businessAddress.postalCode}
                </span>
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/80">
                <Clock className="h-5 w-5 text-accent shrink-0 mt-0.5" aria-hidden="true" />
                <span>{BUSINESS_HOURS_LABEL}</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Services</h3>
            <ul className="space-y-3">
              {footerServices.map(s => (
                <li key={s.slug}>
                  <Link href={`/services/${s.slug}`} onClick={scrollTop} className={`text-primary-foreground/80 ${s.isEmergency ? 'hover:text-accent font-medium flex items-center gap-2' : 'hover:text-white'} transition-colors`}>
                    {s.isEmergency && <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>}
                    {s.shortTitle}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/services/filtration-eau/" onClick={scrollTop} className="text-primary-foreground/80 hover:text-white transition-colors">
                  Filtration d'eau
                </Link>
              </li>
              <li>
                <Link href="/services" onClick={scrollTop} className="text-primary-foreground/60 hover:text-accent transition-colors text-sm mt-2 inline-block">
                  Voir tous les services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Zones */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Nos zones</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/plombier-vieux-longueuil" onClick={scrollTop} className="text-primary-foreground/80 hover:text-white transition-colors">
                  Vieux-Longueuil
                </Link>
              </li>
              {cities.filter((c) => c.slug !== "vieux-longueuil").map(c => (
                <li key={c.slug}>
                  <Link href={`/plombier-${c.slug}`} onClick={scrollTop} className="text-primary-foreground/80 hover:text-white transition-colors">
                    {c.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/plombier-rive-sud" onClick={scrollTop} className="text-primary-foreground/80 hover:text-white transition-colors">
                  Rive-Sud de Montréal
                </Link>
              </li>
              <li>
                <Link href="/zones" onClick={scrollTop} className="text-primary-foreground/80 hover:text-accent transition-colors font-medium mt-2 inline-block">
                  Toutes nos zones →
                </Link>
              </li>
            </ul>
          </div>

        </div>

        {/* Bannière avis Google */}
        <div className="border-t border-primary-foreground/20 pt-8 mb-6">
          <div className="inline-flex flex-wrap items-center gap-3 bg-white/10 border border-white/20 rounded-xl px-5 py-3">
            <div className="flex gap-0.5" aria-hidden="true">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-white font-semibold text-sm">
              Google : {googleReviewSummary.rating.toFixed(1).replace(".", ",")}/5 · {googleReviewSummary.reviewCount} avis
            </span>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 pt-6 flex flex-col md:flex-row items-center justify-between text-primary-foreground/60 text-sm gap-3">
          <p>&copy; {new Date().getFullYear()} Plomberie Excellence. Tous droits réservés.</p>
          <div className="flex gap-4">
            <Link href="/termes-et-conditions" onClick={scrollTop} className="hover:text-accent transition-colors">Termes et conditions</Link>
            <Link href="/politique-de-confidentialite" onClick={scrollTop} className="hover:text-accent transition-colors">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
      <div className="px-4 pb-5 text-center">
        <button type="button" onClick={() => window.dispatchEvent(new Event("plomberie:cookie-preferences"))} className="min-h-11 text-sm text-primary-foreground/80 underline underline-offset-4 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
          Préférences de cookies
        </button>
      </div>
    </footer>
  );
}
